/**
 * Session handoff — the producer for the `#__session` fragment.
 *
 * `src/lib/editor/session.ts` has always consumed a `#__session=<jwt>`
 * fragment and nothing has ever written one (#151), so `handoff` mode — the
 * mode localhost and every Vercel preview run in — could never obtain a
 * session. Clerk's cookie is scoped to `.manticscore.com`, so those origins
 * cannot read it and there was no other route in.
 *
 * This module is the security boundary for the fix. A page that mints a live
 * session token and forwards it to a URL is an account-takeover vector if the
 * destination is attacker-controlled: get a signed-in user to open
 * `/handoff?returnTo=https://attacker.example` and you have their token. So
 * the destination is checked against an exact-origin allowlist, and anything
 * not on it is refused rather than redirected to.
 */

/**
 * Origins allowed to receive a session token.
 *
 * Exact origins, compared with `===`. Not prefixes, not suffixes, not
 * patterns.
 *
 * **`*.vercel.app` is deliberately absent even though preview deploys need
 * this.** `vercel.app` is a public shared suffix — anyone can deploy to it, so
 * a wildcard would let any attacker with a free Vercel account receive tokens
 * from anyone they can get to click a link. Previews have to be added as exact
 * origins if they are wanted, which is a decision worth making explicitly
 * rather than inheriting from a glob.
 */
export const ALLOWED_RETURN_ORIGINS: readonly string[] = [
  "https://manticscore.com",
  "https://www.manticscore.com",
  "http://localhost:8080",
  "http://127.0.0.1:8080",
];

/**
 * Whether `returnTo` may receive a token.
 *
 * Rejects anything that does not parse, anything not http(s) — `javascript:`
 * and `data:` URLs parse fine and would execute — and any origin not on the
 * list. Defaults to refusing: a value this function cannot confidently
 * classify is not allowed.
 */
export function isAllowedReturnTo(
  returnTo: string | null | undefined,
  allowed: readonly string[] = ALLOWED_RETURN_ORIGINS,
): boolean {
  if (!returnTo) return false;
  let url: URL;
  try {
    url = new URL(returnTo);
  } catch {
    return false;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return false;
  return allowed.includes(url.origin);
}

/** The production origin that owns the Clerk cookie and can therefore mint. */
export const HANDOFF_ORIGIN = "https://manticscore.com";

/**
 * Where a handoff-mode client sends the user to get a session.
 *
 * Always the production origin, never the current one: an origin that could
 * mint for itself would not be in handoff mode in the first place.
 */
export function handoffUrlFor(returnTo: string): string {
  return `${HANDOFF_ORIGIN}/handoff?returnTo=${encodeURIComponent(returnTo)}`;
}

/**
 * The redirect target carrying the token.
 *
 * A fragment rather than a query parameter on purpose: fragments are not sent
 * to the server, so the token stays out of access logs, `Referer` headers and
 * anything else that records a URL. `consumeSessionHandoff` strips it from the
 * address bar on arrival.
 *
 * Throws rather than returning a fallback if the destination is not allowed —
 * a caller that forgets to check must fail loudly, not redirect somewhere
 * unintended.
 */
export function buildHandoffTarget(
  returnTo: string,
  token: string,
  allowed: readonly string[] = ALLOWED_RETURN_ORIGINS,
): string {
  if (!isAllowedReturnTo(returnTo, allowed)) {
    throw new Error("refusing to hand a session token to a non-allowlisted origin");
  }
  const url = new URL(returnTo);
  // Replace rather than append: an inbound `#...` on the returnTo is
  // attacker-controllable and must not survive next to the token.
  url.hash = `__session=${encodeURIComponent(token)}`;
  return url.toString();
}
